import { EditEntitySection } from '@/modules/entities/EditEntitySplitScreen/EditEntitySplitScreen.types';
import {
  BUSINESS_ENTITY_KINDS,
  NON_TRUST_CHARITABLE_ENTITY_KINDS,
  PERSONAL_ACCOUNT_ENTITY_KINDS,
  TRUST_ENTITY_KINDS,
} from '@/modules/entities/entities.constants';
import { EntityKind } from '@/types/schema';

export interface EntityCoverGrayBlockEditConfig {
  /** Toolbar button label, phrased for the people shown in the gray block. */
  label: string;
  /** Edit-entity section the button deep-links into. */
  section: EditEntitySection;
}

/**
 * The gray block on the entity cover slide lists the people associated with an
 * entity, and the list differs by entity category. This maps an entity kind to
 * the single edit action shown over that block: the label that matches what's
 * listed, and the edit-entity section it opens.
 *
 * - Trusts (incl. CLT/CRT, which list trustees + trust advisors) → Trustees.
 * - Businesses (key people) → the ownership & key-people details section.
 * - Foundations/DAFs and personal accounts (controlling persons/signatories,
 *   which live in the basic-information subform) → Basic Information.
 *
 * Returns null for kinds with no associated-people block.
 */
export function getEntityCoverGrayBlockEditConfig(
  kind: EntityKind
): EntityCoverGrayBlockEditConfig | null {
  if (TRUST_ENTITY_KINDS.includes(kind)) {
    return {
      label: 'Edit trustees and trust advisors',
      section: EditEntitySection.TRUSTEES,
    };
  }
  if (BUSINESS_ENTITY_KINDS.includes(kind)) {
    return {
      label: 'Edit key people',
      section: EditEntitySection.BUSINESS_ENTITY_DETAILS,
    };
  }
  if (
    NON_TRUST_CHARITABLE_ENTITY_KINDS.includes(kind) ||
    PERSONAL_ACCOUNT_ENTITY_KINDS.includes(kind)
  ) {
    return {
      label: 'Edit controlling persons and signatories',
      section: EditEntitySection.BASIC_INFORMATION,
    };
  }
  return null;
}
